import {Node} from 'reactflow';

import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  NewTaskDefinitionVersionSpec,
  TaskDefinitionVersionSpec,
  TaskSchemaSpec,
} from 'processes/dao/ProcessFlowchartConfigDAO';
import {RouterConfig} from 'processes/modals/router-config/RouterConfigConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {
  NodeSection,
  CoreBenchlingNodeData,
  BenchlingNodeCustomization,
} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeTypes';

import {
  ProcessFlowchartNodeType,
  TaskSchemaNodeSectionType,
  SidepanelTab,
} from './processFlowchartConfigEditorConstants';

// Task schemas aren't needed for network specs, but it is useful to co-locate with task definition versions
// on the frontend for convenience.
interface NewTaskDefinitionVersionSpecWithTaskSchema<TApplicationData = unknown>
  extends NewTaskDefinitionVersionSpec<TApplicationData> {
  taskSchema: TaskSchemaSpec;
}

// This shape will get converted to the network spec shape in the DAO.
export interface TaskDefinitionVersionSpecWithTaskSchema<TApplicationData = unknown>
  extends Omit<TaskDefinitionVersionSpec<TApplicationData>, 'newProcessTaskDefinitionVersion'> {
  newProcessTaskDefinitionVersion: NewTaskDefinitionVersionSpecWithTaskSchema<TApplicationData>;
}

interface TaskSchemaDomainData {
  id: string;
  nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE;
  nodeDetailsType: ProcessFlowchartNodeDetailsType;
  taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema;
  taskSchemaId: string;
}

export interface TaskSchemaNodeRowData {
  id: string;
  clientId: string;
  isFlowchartTask: boolean;
  sectionName: string;
  sectionType: TaskSchemaNodeSectionType;
  fieldName: string;
  fieldIcon: BenchlingIconName;
  fieldTypeLabel: string | JSX.Element;
  isSelected?: boolean;
}

export interface TaskSchemaNodeSectionData extends NodeSection<TaskSchemaNodeRowData> {
  sectionType: TaskSchemaNodeSectionType;
}
interface ExtendedCoreBenchlingNodeData<TNodeSectionData>
  extends Omit<CoreBenchlingNodeData<TaskSchemaNodeRowData>, 'sections'> {
  sections: Array<TNodeSectionData>;
}
type BaseNodeData<TNodeSectionData, TNodeRowData> = ExtendedCoreBenchlingNodeData<TNodeSectionData> &
  BenchlingNodeCustomization<TNodeRowData>;

type TaskSchemaNodeBaseData = BaseNodeData<TaskSchemaNodeSectionData, TaskSchemaNodeRowData>;
type NonSectionedNodeBaseData = BaseNodeData<never, never>;

export interface TaskSchemaInteractionNodeData {
  onNodeHeaderClick: (nodeId: string) => void;
  nodeDetailsSidepanelContent: Array<SidepanelTab>;
  onFieldClick: (nodeId: string, fieldId: string) => void;
  rowRenderer: TaskSchemaNodeBaseData['rowRenderer'];
  nodeHeaderHandleRenderer: TaskSchemaNodeBaseData['nodeHeaderHandleRenderer'];
}

interface NonSectionedNodeInteractiveData {
  rowRenderer: NonSectionedNodeBaseData['rowRenderer'];
  onNodeHeaderClick: (nodeId: string) => void;
  nodeHeaderHandleRenderer: NonSectionedNodeBaseData['nodeHeaderHandleRenderer'];
}

/**
 * Static data that is used for converting from and persisting task schemas to the database.
 *
 * Removes fields that should not be populated when converting from a task schema to a node.
 */
export type TaskSchemaStaticNodeData = TaskSchemaDomainData &
  Omit<
    TaskSchemaNodeBaseData,
    'rowRenderer' | 'nodeHeaderHandleRenderer' | 'nodeStatus' | 'onNodeHeaderClick'
  >;
/**
 * Populated data to be passed to the base node renderer.
 *
 * Adds fields that are specific to flowchart visualization and interaction, that are not needed
 * when converting from a task schema to a node or persisting to the database.
 */
export type TaskSchemaNodeData = TaskSchemaStaticNodeData & TaskSchemaInteractionNodeData;
export type TaskSchemaNode = Node<TaskSchemaNodeData>;

export type RouterNodeData = RouterStaticNodeData & NonSectionedNodeInteractiveData;
export type RouterNode = Node<RouterNodeData>;

export type EndNodeData = EndStaticNodeData & NonSectionedNodeInteractiveData;
export type EndNode = Node<EndNodeData>;

interface PlaceholderForTaskSchemaCreateData {
  placeholderForNodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE;
  executionType: ProcessExecutionType;
}

type PlaceholderForNodeTypeData = PlaceholderForTaskSchemaCreateData;

export type PlaceholderNodeStaticData = Omit<
  NonSectionedNodeBaseData,
  'rowRenderer' | 'nodeHeaderHandleRenderer' | 'nodeStatus' | 'onNodeHeaderClick'
> & {
  id: string;
  nodeType: ProcessFlowchartNodeType.PLACEHOLDER_NODE;
  placeholderForNodeTypeData: PlaceholderForNodeTypeData;
  nodeName: string;
  nodeIcon: string;
};

export type PlaceholderNodeData = PlaceholderNodeStaticData & NonSectionedNodeInteractiveData;
export type PlaceholderNode = Node<PlaceholderNodeData>;

export type RouterStaticNodeData = Omit<
  NonSectionedNodeBaseData,
  'rowRenderer' | 'nodeHeaderHandleRenderer' | 'nodeStatus' | 'onNodeHeaderClick'
> & {
  id: string;
  nodeType: ProcessFlowchartNodeType.ROUTER_NODE;
  nodeDetailsType: ProcessFlowchartNodeDetailsType;
  nodeName: string;
  nodeIcon: string;
  routerConfig: RouterConfig;
};

export type EndStaticNodeData = Omit<
  NonSectionedNodeBaseData,
  'rowRenderer' | 'nodeHeaderHandleRenderer' | 'nodeStatus' | 'onNodeHeaderClick'
> & {
  id: string;
  nodeName: string;
  nodeType: ProcessFlowchartNodeType.STOP_NODE;
  nodeDetailsType: ProcessFlowchartNodeDetailsType;
  nodeIcon: string;
};

export type StaticFlowchartNodeData =
  | TaskSchemaStaticNodeData
  | RouterStaticNodeData
  | EndStaticNodeData
  | PlaceholderNodeStaticData;

export type FlowchartNode = Node<StaticFlowchartNodeData>;

export function isInteractiveTaskNode(node: FlowchartNode): node is TaskSchemaNode {
  return isStaticTaskSchemaNodeData(node.data);
}

export function isInteractiveRouterNode(node: FlowchartNode): node is RouterNode {
  return isStaticRouterNodeData(node.data);
}

export function isInteractiveEndNode(node: FlowchartNode): node is EndNode {
  return isStaticEndNodeData(node.data);
}

export function isInteractivePlaceholderNode(node: FlowchartNode): node is PlaceholderNode {
  return isStaticPlaceholderNodeData(node.data);
}

export function isStaticTaskSchemaNodeData(
  nodeData: StaticFlowchartNodeData
): nodeData is TaskSchemaStaticNodeData {
  return nodeData.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE;
}

export function isStaticRouterNodeData(nodeData: StaticFlowchartNodeData): nodeData is RouterStaticNodeData {
  return nodeData.nodeType === ProcessFlowchartNodeType.ROUTER_NODE;
}

export function isStaticEndNodeData(nodeData: StaticFlowchartNodeData): nodeData is EndStaticNodeData {
  return nodeData.nodeType === ProcessFlowchartNodeType.STOP_NODE;
}

export function isStaticPlaceholderNodeData(
  nodeData: StaticFlowchartNodeData
): nodeData is PlaceholderNodeStaticData {
  return nodeData.nodeType === ProcessFlowchartNodeType.PLACEHOLDER_NODE;
}

/**
 * Maps a node type to its corresponding full node data types
 */
export interface FlowchartNodeTypeToNodeDataMap {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: TaskSchemaNodeData;
  [ProcessFlowchartNodeType.ROUTER_NODE]: RouterNodeData;
  [ProcessFlowchartNodeType.STOP_NODE]: EndNodeData;
  [ProcessFlowchartNodeType.PLACEHOLDER_NODE]: PlaceholderNodeData;
}

/**
 * Maps static node data types to their corresponding ReactFlow Node types
 */
export interface FlowchartNodeTypeToReactflowNodeMap {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: TaskSchemaNode;
  [ProcessFlowchartNodeType.ROUTER_NODE]: RouterNode;
  [ProcessFlowchartNodeType.STOP_NODE]: EndNode;
  [ProcessFlowchartNodeType.PLACEHOLDER_NODE]: PlaceholderNode;
}

/**
 * Maps node types to their corresponding static data types
 */
export interface NodeTypeToStaticNodeDataMap {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: TaskSchemaStaticNodeData;
  [ProcessFlowchartNodeType.ROUTER_NODE]: RouterStaticNodeData;
  [ProcessFlowchartNodeType.STOP_NODE]: EndStaticNodeData;
  [ProcessFlowchartNodeType.PLACEHOLDER_NODE]: PlaceholderNodeStaticData;
}

/**
 * Helper type for getting the correct interactive data needed for a specific node type
 */
export type NodeForNodeType<T extends ProcessFlowchartNodeType> = FlowchartNodeTypeToReactflowNodeMap[T];

/**
 * Helper type for getting the static data type for a specific node type
 */
export type StaticDataForNodeType<T extends ProcessFlowchartNodeType> = NodeTypeToStaticNodeDataMap[T];

/**
 * Extract the node type from static data using the discriminant property
 */
export type ExtractNodeType<T extends StaticFlowchartNodeData> = T['nodeType'];

/**
 * Get the corresponding Node type for a given static data type
 */
export type StaticDataToNode<T extends StaticFlowchartNodeData> =
  FlowchartNodeTypeToReactflowNodeMap[ExtractNodeType<T>];
